import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, debounceTime, first, map, skip, takeUntil } from 'rxjs';
import { PremiumCalculationService } from 'src/app/services/premium-calculation.service';
import { environment } from '../../environments/environment';
import { ApiState } from '../../model/api-state.model';
import AppError from '../../model/app-error';
import { AppType } from '../../model/app-type.model';
import { QuotationPackage } from '../../model/offer';
import { ProcessType } from '../../model/process-type.model';
import { FormsService } from '../../services/forms.service';
import { PersistStorageKeys, PersistStorageService } from '../../services/persist-storage.service';
import { ProcessService } from '../../services/process.service';
import { AppState } from '../../store';
import * as fromProcess from '../../store/process/process.reducer';
import { TrackingService } from '../../tracking/tracking.service';
import { formatterCurrency } from '../../utils';
import { GtmService } from '../../services/gtm.service';

export const BACKOFFICE_PERMISSION = '4';
const SKIP_INIT_STORE_VALUE = 1;
const ADDOND_REFRESH_DEBOUNCE_TIME = 200;
@Component({
  selector: 'app-page-offer',
  templateUrl: './offer.component.html',
  styleUrls: ['./offer.component.scss'],
})
export class PageOfferComponent implements OnInit, OnDestroy {
  isAppTravelAgency = environment.app === AppType.TravelAgency;
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  isResignation$ = this.processKind$.pipe(map((processKind) => processKind === ProcessType.RESIGNATION));
  quotationState$ = this.store.select(fromProcess.selectOfferQuotationState);
  quotationData$ = this.store.select(fromProcess.selectOfferQuotationData);
  quotationError$ = this.store.select(fromProcess.selectOfferQuotationError);
  offerPackages$ = this.store.select(fromProcess.selectOfferPackages);
  selectedPackage$ = this.store.select(fromProcess.selectOfferSelectedPackage);
  selectedTab$ = this.store.select(fromProcess.selectOfferSelectedPackageIndex);
  selectedAgentSection$ = this.store.select(fromProcess.selectAgentSection);
  selectedCustomizeAlcoholIncluded$ = this.store.select(fromProcess.selectCustomizeAlcoholIncluded);
  discountCode$ = this.store.select(fromProcess.selectOfferDiscountCode);
  packageAddons$ = this.store.select(fromProcess.selectPackageAddons);
  premiumCalculation$ = this.store.select(fromProcess.selectPremiumCalculation);
  premiumCalculationState$ = this.store.select(fromProcess.selectPremiumCalculationState);
  customizeAlcoholIncluded$ = this.store.select(fromProcess.selectCustomizeAlcoholIncluded);
  offerValue$ = this.store.select(fromProcess.selectOfferValue);
  isOfferPriceBoxVisible$ = this.store.select(fromProcess.selectIsOfferPriceBoxVisible);
  currentStep$ = this.store.select(fromProcess.selectProcessStep);
  backofficeForm = this.formsService.backofficeSection;
  showToolbox = false;
  showToolboxMessage = false;

  ngDestroy$ = new Subject<void>();

  apiState = ApiState;

  constructor(
    private store: Store<AppState>,
    private formsService: FormsService,
    private processService: ProcessService,
    private persistStorage: PersistStorageService,
    private trackingService: TrackingService,
    private premiumCalculationService: PremiumCalculationService,
    private gtmService: GtmService,
  ) {}

  ngOnInit(): void {
    this.store.dispatch(fromProcess.actions.setStep({ step: 2 }));
    this.processService.onPageInit();
    this.showToolbox = this.persistStorage.getString(PersistStorageKeys.AgentCategory) === BACKOFFICE_PERMISSION;

    this.fetchOffers();
    this.changedAddons();
    this.changeDiscountCode();
    this.changeAgent();
    this.implementTracking();
    this.gtmService.step3Event();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  changeTab({ index }: { index: number }): void {
    this.offerPackages$.pipe(first()).subscribe((offerPackages) => {
      const packageName = offerPackages?.[index];

      this.store.dispatch(fromProcess.actions.selectPackage({ packageName: packageName?.codeName || '' }));
    });
  }

  getPackageDescription(quotationPackage: QuotationPackage): Observable<string> {
    return combineLatest([this.quotationState$, this.quotationData$, this.processKind$]).pipe(
      map(([quotationState, quotationData, processKind]) => {
        switch (quotationState) {
          case ApiState.success: {
            const { amount } = quotationPackage.totalValue;
            const premiumAmount =
              processKind &&
              quotationData?.result.path[processKind].packages.find((packageOption) => packageOption.codeName === quotationPackage.codeName)
                ?.totalValue.amount;

            return formatterCurrency(premiumAmount || amount, quotationPackage.totalValue.currency);
          }
          case ApiState.error: {
            return '—';
          }
          default:
            return '…';
        }
      }),
    );
  }

  saveBackOfficeSection() {
    this.formsService.backofficeSection.markAllAsTouched();

    if (this.formsService.backofficeSection.invalid) {
      return;
    }

    this.showToolboxMessage = true;
    this.fetchOffers();
    this.store.dispatch(fromProcess.actions.setSendBackOfficeData({ sendBackOfficeData: true }));
  }

  private changedAddons(): void {
    this.packageAddons$
      .pipe(takeUntil(this.ngDestroy$), skip(SKIP_INIT_STORE_VALUE), debounceTime(ADDOND_REFRESH_DEBOUNCE_TIME))
      .subscribe(() => {
        this.fetchOffers();
      });
  }

  private changeDiscountCode(): void {
    this.discountCode$.pipe(takeUntil(this.ngDestroy$), skip(SKIP_INIT_STORE_VALUE)).subscribe(() => {
      this.fetchOffers();
    });
  }

  private changeAgent(): void {
    this.selectedAgentSection$.pipe(takeUntil(this.ngDestroy$), skip(SKIP_INIT_STORE_VALUE)).subscribe(() => {
      this.fetchOffers();
    });
  }

  private fetchOffers(slimResponse: boolean = false): void {
    this.premiumCalculationService.quotationBody$.pipe(first()).subscribe((body) => {
      if (!body) {
        this.store.dispatch(fromProcess.actions.fetchQuotationError({ error: new AppError('app', null, '') }));

        return;
      }

      this.store.dispatch(fromProcess.actions.fetchQuotationStart({ body, slim: slimResponse }));
    });
  }

  private implementTracking(): void {
    this.processKind$.pipe(takeUntil(this.ngDestroy$)).subscribe((processKind) => {
      switch (processKind) {
        case ProcessType.TRIPABROAD:
          this.trackingService.productInfoPrimaryCategory('Travel');
          break;
        case ProcessType.TRAVELINPL:
          this.trackingService.productInfoPrimaryCategory('Travel');
          break;
        case ProcessType.RESIGNATION:
          this.trackingService.productInfoPrimaryCategory('Resignation');
          break;
        default:
          this.trackingService.productInfoPrimaryCategory(null);
          break;
      }
    });

    this.selectedPackage$.pipe(takeUntil(this.ngDestroy$)).subscribe((selectedPackage) => {
      this.trackingService.productName(selectedPackage?.codeName || null);
    });

    this.offerValue$.pipe(takeUntil(this.ngDestroy$)).subscribe((offerValue) => {
      this.trackingService.productPrice(offerValue || null);
    });
  }
}
